import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { NetworkHelper } from '../helpers';
import { SHARED_ENV } from '../tokens';

@Injectable({
  providedIn: 'root',
})
export class CurrencyCacheService {
  private readonly networkHelper = inject(NetworkHelper);
  private readonly env = inject(SHARED_ENV);

  private static cache: Record<string, any> = {};
  private static symbols: Record<string, any> = {};

  readonly currencies$ = new BehaviorSubject<any[]>([]);

  static clear(): void {
    CurrencyCacheService.cache = {};
    CurrencyCacheService.symbols = {};
  }

  static get(symbol: string): any {
    return CurrencyCacheService.cache[symbol];
  }

  static getAll(): Record<string, any> {
    return CurrencyCacheService.cache;
  }

  static getSymbol(symbol: string): any {
    return CurrencyCacheService.symbols[symbol];
  }

  static getSymbolsFromCache(): Record<string, any> {
    return CurrencyCacheService.symbols;
  }

  static setSymbols(data: any[]): void {
    for (const item of data) {
      if (item?.symbol) CurrencyCacheService.symbols[item.symbol] = item;
    }
  }

  static getPrecision(symbol: string): number {
    return CurrencyCacheService.cache[symbol]?.amountPrecision ?? 0;
  }

  static getPricePrecision(symbol: string): number {
    return CurrencyCacheService.cache[symbol]?.pricePrecision ?? 0;
  }

  // Precision withdrawal amounts are truncated to; unset means amountPrecision.
  static getWithdrawalPrecision(symbol: string): number {
    const currency = CurrencyCacheService.cache[symbol];
    return (
      currency?.withdrawalAmountPrecision ?? currency?.amountPrecision ?? 0
    );
  }

  static getName(symbol: string): string {
    return CurrencyCacheService.cache[symbol]?.name ?? symbol;
  }

  static getType(symbol: string): 'crypto' | 'fiat' | '' {
    return CurrencyCacheService.cache[symbol]?.type ?? '';
  }

  static isActive(symbol: string): boolean {
    return CurrencyCacheService.cache[symbol]?.isActive ?? false;
  }

  static isDepositActive(symbol: string): boolean {
    return CurrencyCacheService.cache[symbol]?.depositActive ?? false;
  }

  static isWithdrawActive(symbol: string): boolean {
    return CurrencyCacheService.cache[symbol]?.withdrawActive ?? false;
  }

  loadCurrencies(): Promise<void> {
    return firstValueFrom(
      this.networkHelper.get<any[]>(`${this.env.host}/currencies`)
    )
      .then((result) => {
        if (result && Array.isArray(result)) {
          for (const item of result) {
            if (item?.symbol) CurrencyCacheService.cache[item.symbol] = item;
          }
          this.currencies$.next(result);
        }
      })
      .catch(() => {});
  }
}
